import {RpcFilterT, useViewQuery} from '@tnc/shared';
import {useRef, useState} from 'react';
import {useNavigate} from 'react-router';

import {statsRowT} from '@/lib/types/stats.types';

import {dateFormatter, usdFormatter} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent, LoadingSpinningLogoComponent, StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTableRpc, DataTableRpcRefT} from '@/components/dataTableRpc';
import DefaultTableAvatarImage from '@/components/defaultTableAvatarImage';

import BuySellTransferList from './components/buySellTransferList';
import ExchangeOrderProvider from './providers/exchangeOrder';

function SpeculatorPositionsPage() {
  // Hooks
  const navigate = useNavigate();
  const {toast} = useNotifications();
  const {
    userAccounts: {speculator},
  } = useAuth();

  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [positionId, setPositionId] = useState<number | null>(null);
  const [tableFilters, setTableFilters] = useState<RpcFilterT<'get_positions_with_quantity'>[]>([
    {column: 'fk_speculator_id', operator: 'eq', value: speculator?.speculator_id || 1},
  ]);

  // TanStack queries
  const {data: stats, isLoading} = useViewQuery({
    view: 'speculator_position_stats',
    filters: [
      {
        column: 'speculator_id',
        operator: 'eq',
        value: speculator?.speculator_id || 0,
      },
    ],
    single: true,
  });

  // Stats for the stats component
  const statsRow: statsRowT = [
    {
      title: 'Total Positions',
      value: stats?.data[0]?.total ?? 0,
      subtitle: 'This Month',
      icon: 'ri-stack-line',
      color: 'primary',
      change: 1.03,
      cardClass: 'Stats-main-card primary',
    },
    {
      title: 'Total Retail Price',
      value: usdFormatter.format(stats?.data[0]?.totalretailprice ?? 0),
      subtitle: 'This Month',
      icon: 'ri-money-dollar-circle-line',
      color: 'warning',
      change: -1.28,
      cardClass: 'Stats-main-card warning',
    },
    {
      title: 'Potential Profit',
      value: usdFormatter.format(stats?.data[0]?.potentialprofitchange ?? 0),
      subtitle: 'This Month',
      icon: 'ri-money-dollar-box-line',
      color: 'secondary',
      // change is the total potential profit / total retail price
      change:
        (stats?.data[0]?.potentialprofitchange && Number((stats?.data[0]?.potentialprofitchange * 100).toFixed(2))) ||
        0,
      cardClass: 'Stats-main-card secondary',
    },
  ];

  const handleClosePosition = async (positionId: number) => {
    const {data: user} = await SupabaseInstance.supabase.auth.getUser();

    if (!user.user?.id || !speculator?.speculator_id) return;

    const {error} = await SupabaseInstance.supabase.functions.invoke('close-position', {
      body: {
        position_id: positionId,
        user_uid: user.user.id,
        speculator_id: speculator.speculator_id,
      },
    });

    if (error) {
      console.error('RPC Error:', error);
    } else {
      toast.success('Position closed successfully');
    }
  };

  /**
   * closes the position
   */
  const onClosePosition = async () => {
    if (!positionId) return;
    await handleClosePosition(positionId);
    setDeleteModalVisible(false);
  };

  /**
   * handles setting the filters for the datatable
   */
  const handleSetTableFilters = (filters: RpcFilterT<'get_positions_with_quantity'>[]) => {
    setTableFilters(filters);
  };

  // Create a ref for the DataTableRpc
  const dataTableRef = useRef<DataTableRpcRefT>(null);

  if (isLoading) return <LoadingSpinningLogoComponent />;

  return (
    <ExchangeOrderProvider>
      <Page currentPage="Positions">
        {stats && <StatsRowComponent stats={statsRow} />}
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="lg:w-2/3">
            <DataTableRpc
              ref={dataTableRef}
              title="Positions Summary"
              functionName="get_positions_with_quantity"
              limit={50}
              filters={tableFilters}
              customFilterButtons={[
                {
                  label: 'Buy',
                  onClick: () => {
                    if (
                      tableFilters.some(filter => filter.column === 'position_order_type' && filter.value === 'buy')
                    ) {
                      // Remove buy filter
                      handleSetTableFilters(tableFilters.filter(filter => filter.column !== 'position_order_type'));
                    } else {
                      // Remove any existing position_order_type filter and add buy
                      handleSetTableFilters([
                        ...tableFilters.filter(filter => filter.column !== 'position_order_type'),
                        {column: 'position_order_type', operator: 'eq', value: 'buy'},
                      ]);
                    }
                  },
                  color: 'success',
                  active: tableFilters.some(
                    filter => filter.column === 'position_order_type' && filter.value === 'buy'
                  ),
                },
                {
                  label: 'Sell',
                  onClick: () => {
                    if (
                      tableFilters.some(filter => filter.column === 'position_order_type' && filter.value === 'sell')
                    ) {
                      // Remove sell filter
                      handleSetTableFilters(tableFilters.filter(filter => filter.column !== 'position_order_type'));
                    } else {
                      // Remove any existing position_order_type filter and add sell
                      handleSetTableFilters([
                        ...tableFilters.filter(filter => filter.column !== 'position_order_type'),
                        {column: 'position_order_type', operator: 'eq', value: 'sell'},
                      ]);
                    }
                  },
                  color: 'danger',
                  active: tableFilters.some(
                    filter => filter.column === 'position_order_type' && filter.value === 'sell'
                  ),
                },
              ]}
              columns={[
                {
                  header: 'Product',
                  field: 'product_name',
                  render: row => (
                    <div className="flex min-w-[150px] gap-2">
                      <DefaultTableAvatarImage
                        src={row.product_image_url || row.brand_logo_url || '/images/logo/logo.png'}
                        alt="Product Logo"
                      />
                      <div className="flex flex-col">
                        <span>{row.product_name}</span>
                        <span className="text-muted text-truncate text-gray text-xs text-textmuted">
                          {row.brand_name}
                        </span>
                      </div>
                    </div>
                  ),
                },
                {
                  header: 'Type',
                  field: 'position_order_type',
                  render: row => (
                    <span
                      className={`text-capitalize ${
                        row.position_order_type === 'buy'
                          ? 'text-success'
                          : row.position_order_type === 'sell'
                            ? 'text-danger'
                            : 'text-info'
                      }`}
                    >
                      {row.position_order_type}
                    </span>
                  ),
                },
                {
                  header: 'Market',
                  field: 'market_name',
                },
                {
                  header: 'Filled',
                  field: 'position_is_filled',
                  render: row => (
                    <span className={`badge bg-${row.position_is_filled ? 'success' : 'danger'} text-white`}>
                      {row.position_is_filled ? 'True' : 'False'}
                    </span>
                  ),
                },
                {
                  header: 'Status',
                  field: 'position_status',
                  render: row => (
                    <span className={`badge bg-${row.position_status === 'closed' ? 'danger' : 'success'} text-white`}>
                      {row.position_status}
                    </span>
                  ),
                },
                {
                  header: 'Bid/Ask Price',
                  field: 'position_price',
                  render: row => (
                    <span>{`${row.position_order_type === 'buy' ? 'Bid: ' : 'Ask: '} $${row.position_price}`}</span>
                  ),
                },
                {
                  header: 'Total Items',
                  field: 'position_item_quantity',
                },
                {
                  header: 'Remaining Fill',
                  field: 'position_order_remaining_quantity',
                  render: row => <span>{row.position_order_remaining_quantity}</span>,
                },
                {
                  header: 'Pending Inv',
                  field: 'pending_inventory_count',
                },
                {
                  header: 'Retail Inv',
                  field: 'retail_inventory_count',
                },
                {
                  header: 'Distributor Inv',
                  field: 'distributor_inventory_count',
                },
                {
                  header: 'Position Balance',
                  field: 'position_balance',
                  render: row => (
                    <span className={`${row.position_balance > 0 ? 'text-success' : 'text-danger'}`}>
                      ${row.position_balance}
                    </span>
                  ),
                },
                {
                  header: 'Market Cost Unit',
                  field: 'position_item_unit_market_cost',
                  render: row => <span>{usdFormatter.format(row.position_item_unit_market_cost)}</span>,
                },
                {
                  header: 'Factored Cost Total',
                  field: 'position_factored_price_total',
                  render: row => <span>{usdFormatter.format(row.position_factored_price_total)}</span>,
                },
                {
                  header: 'Margin ($)',
                  field: 'position_margin_total',
                  render: row => <span className="text-success">{usdFormatter.format(row.position_margin_total)}</span>,
                },
                {
                  header: 'Timestamp',
                  field: 'date_created',
                  render: row => <span>{dateFormatter(row.date_created, 'long')}</span>,
                },
              ]}
              enabled={!!speculator?.speculator_id}
              actions={[
                {
                  icon: 'bi bi-arrows-angle-expand',
                  color: 'info',
                  handler: row => {
                    navigate(`/positions/${row.position_id}`);
                  },
                  permission: () => true,
                },
                {
                  icon: 'ri-close-line',
                  color: 'danger',
                  handler: row => {
                    setPositionId(row.position_id);
                    setDeleteModalVisible(true);
                  },
                  permission: () => true,
                  isShown: row => row.position_status === 'active',
                },
              ]}
              sortOptions={[{column: 'date_created', direction: 'desc', label: 'Newest'}]}
              hideAdd
              // onAdd={() => {
              //   navigate('/speculator/positions/new');
              // }}
              className=" "
            />
          </div>
          <div className="lg:w-1/3">
            <BuySellTransferList className="lg:w-full" onPositionFilled={() => dataTableRef.current?.refetch()} />
          </div>
        </div>
      </Page>

      <ConfirmDeleteModalComponent
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        title="Confirm Deletion"
        description="Are you sure you want to close this position?"
        onConfirm={onClosePosition}
      />
    </ExchangeOrderProvider>
  );
}

export default SpeculatorPositionsPage;
